import { DietType, type Traits, type Vec2 } from './types'
import type { Food } from './food'

let nextId = 0

const MAX_ENERGY = 150
const BASE_ENERGY = 80

/** 乱数ヘルパー */
function rand(min: number, max: number): number {
  return min + Math.random() * (max - min)
}

function randDiet(): DietType {
  const r = Math.random()
  if (r < 0.4) return DietType.Herbivore
  if (r < 0.7) return DietType.Carnivore
  return DietType.Omnivore
}

export function randomTraits(): Traits {
  const diet = randDiet()
  // 肉食は速く大きい、草食は遅く小さい、雑食は中間
  const speedBase = diet === DietType.Carnivore ? 2.5 : diet === DietType.Herbivore ? 1.2 : 1.8
  return {
    speed: rand(speedBase * 0.7, speedBase * 1.3),
    visionRange: rand(40, 120),
    size: rand(
      diet === DietType.Carnivore ? 5 : 3,
      diet === DietType.Carnivore ? 9 : 6,
    ),
    diet,
    hue: diet === DietType.Herbivore ? rand(90, 150) : diet === DietType.Carnivore ? rand(0, 30) : rand(200, 280),
    metabolismRate: rand(0.8, 1.2),
  }
}

export class Organism {
  id: number
  pos: Vec2
  vel: Vec2
  energy: number
  age: number
  alive: boolean
  traits: Traits

  private wanderAngle: number
  private stateTimer: number

  constructor(x: number, y: number, traits?: Traits) {
    this.id = nextId++
    this.pos = { x, y }
    this.vel = { x: 0, y: 0 }
    this.energy = BASE_ENERGY
    this.age = 0
    this.alive = true
    this.traits = traits ?? randomTraits()
    this.wanderAngle = Math.random() * Math.PI * 2
    this.stateTimer = 0
  }

  update(dt: number, foods: Food[], organisms: Organism[], worldW: number, worldH: number): void {
    this.age += dt

    // エネルギー消費（速さとサイズに比例）
    const drain = 0.008 * this.traits.metabolismRate * (1 + this.traits.speed * 0.3 + this.traits.size * 0.1)
    this.energy -= drain * dt * 60

    if (this.energy <= 0) {
      this.alive = false
      return
    }

    // 最も近い食料を探す
    const target = this.findTarget(foods, organisms)

    if (target) {
      this.moveToward(target, dt)
    } else {
      this.wander(dt)
    }

    // 境界折り返し
    this.pos.x = ((this.pos.x % worldW) + worldW) % worldW
    this.pos.y = ((this.pos.y % worldH) + worldH) % worldH

    // 食事判定
    this.eat(foods, organisms)

    // エネルギー上限
    if (this.energy > MAX_ENERGY) this.energy = MAX_ENERGY

    this.stateTimer += dt
  }

  private findTarget(foods: Food[], organisms: Organism[]): Vec2 | null {
    const { visionRange, diet } = this.traits
    let best: Vec2 | null = null
    let bestDist = visionRange

    if (diet === DietType.Herbivore || diet === DietType.Omnivore) {
      for (const f of foods) {
        if (!f.alive) continue
        const d = dist(this.pos, f.pos)
        if (d < bestDist) {
          bestDist = d
          best = f.pos
        }
      }
    }

    if (diet === DietType.Carnivore || diet === DietType.Omnivore) {
      for (const o of organisms) {
        if (!o.alive || o.id === this.id) continue
        // 草食か自分より小さい個体を狙う
        if (o.traits.diet === DietType.Herbivore || o.traits.size < this.traits.size * 0.8) {
          const d = dist(this.pos, o.pos)
          if (d < bestDist) {
            bestDist = d
            best = o.pos
          }
        }
      }
    }

    return best
  }

  private moveToward(target: Vec2, dt: number): void {
    const dx = target.x - this.pos.x
    const dy = target.y - this.pos.y
    const len = Math.sqrt(dx * dx + dy * dy) || 1
    const spd = this.traits.speed * 60
    this.vel.x = (dx / len) * spd
    this.vel.y = (dy / len) * spd
    this.pos.x += this.vel.x * dt
    this.pos.y += this.vel.y * dt
  }

  private wander(dt: number): void {
    this.wanderAngle += (Math.random() - 0.5) * 0.3
    const spd = this.traits.speed * 40
    this.vel.x = Math.cos(this.wanderAngle) * spd
    this.vel.y = Math.sin(this.wanderAngle) * spd
    this.pos.x += this.vel.x * dt
    this.pos.y += this.vel.y * dt
  }

  private eat(foods: Food[], organisms: Organism[]): void {
    const { diet, size } = this.traits

    if (diet === DietType.Herbivore || diet === DietType.Omnivore) {
      for (const f of foods) {
        if (!f.alive) continue
        if (dist(this.pos, f.pos) < size + f.radius) {
          this.energy += f.energy
          f.alive = false
        }
      }
    }

    if (diet === DietType.Carnivore || diet === DietType.Omnivore) {
      for (const o of organisms) {
        if (!o.alive || o.id === this.id) continue
        if (o.traits.size >= size * 0.9) continue
        if (dist(this.pos, o.pos) < size + o.traits.size) {
          this.energy += o.energy * 0.6
          o.alive = false
        }
      }
    }
  }
}

function dist(a: Vec2, b: Vec2): number {
  const dx = a.x - b.x
  const dy = a.y - b.y
  return Math.sqrt(dx * dx + dy * dy)
}
